/*
PURPOSE: Editorial display headline — the settled Cormorant Garamond ramp
(weight, colour, tracking) from the /design-sandbox/editorial iterations.
USE CASES:
- Hero headline (scale="display", as="h1") and section headings
  (scale="heading", as="h2") across Home2.
DOMAIN BOUNDARIES:
- Presentation only. Heading text passed as children (supports literal "\n"
  via whitespace-pre-line, matching publicPages copy that embeds newlines).
- No imports from design-sandbox/.
*/

import React from 'react';

interface DisplayHeadingProps {
  as?: 'h1' | 'h2' | 'h3';
  scale?: 'display' | 'heading';
  /** Cap the line measure (default true). Set false when the parent layout
   *  already constrains width (e.g. a grid column) and explicit \n breaks
   *  control wrapping, so the cap doesn't force extra mid-sentence wraps. */
  measure?: boolean;
  children: React.ReactNode;
  className?: string;
}

const DisplayHeading: React.FC<DisplayHeadingProps> = ({
  as: Tag = 'h2',
  scale = 'heading',
  measure = true,
  children,
  className,
}) => {
  const sizeClass = scale === 'display' ? 'text-editorial-display' : 'text-editorial-heading';
  // Cap the headline measure (~the sandbox base1 copy column) so headings
  // wrap as a tight editorial block instead of sprawling the full container.
  // Opt out (measure={false}) when a grid column + explicit \n already
  // control the layout.
  const measureClass = !measure ? '' : scale === 'display' ? 'max-w-2xl' : 'max-w-xl';
  return (
    <Tag
      className={`font-cormorant font-normal text-editorial-muted whitespace-pre-line ${sizeClass} ${measureClass} ${className ?? ''}`}
    >
      {children}
    </Tag>
  );
};

export default DisplayHeading;
